import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from '../types';
import { ROOM_IMAGE_LIMIT, getRoomKey } from '../utils';

export { getRoomKey };

export const EMPTY_IMAGE_PUBLIC_IDS: Array<string> = [];

export interface RoomImage {
  publicId: string;
  title?: string;
}

export type RoomImagesByKey = Record<string, Array<RoomImage>>;

export function getFullRoomNames(roomImagePublicIdsByName: Record<string, Array<string>>, publicId: string) {
  return Object.entries(roomImagePublicIdsByName)
    .filter(([_roomName, publicIds]) => publicIds.length >= ROOM_IMAGE_LIMIT && !publicIds.includes(publicId))
    .map(([roomName]) => roomName);
}

export function getRoomImagePublicIdsByName(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  roomImagesByKey: RoomImagesByKey,
) {
  return propertyRoomTypes.reduce<Record<string, Array<string>>>((roomImagePublicIdsByName, room, index) => {
    if (!room.name) {
      return roomImagePublicIdsByName;
    }

    roomImagePublicIdsByName[room.name] = (roomImagesByKey[getRoomKey(room, index)] ?? []).map(
      (image) => image.publicId,
    );

    return roomImagePublicIdsByName;
  }, {});
}

export function areRoomImagePublicIdsByNameEqual(
  firstRoomImagePublicIdsByName: Record<string, Array<string>>,
  secondRoomImagePublicIdsByName: Record<string, Array<string>>,
) {
  const firstRoomNames = Object.keys(firstRoomImagePublicIdsByName);
  const secondRoomNames = Object.keys(secondRoomImagePublicIdsByName);

  if (firstRoomNames.length !== secondRoomNames.length) {
    return false;
  }

  return firstRoomNames.every((roomName) => {
    const firstPublicIds = firstRoomImagePublicIdsByName[roomName];
    const secondPublicIds = secondRoomImagePublicIdsByName[roomName];

    return (
      !!secondPublicIds &&
      firstPublicIds.length === secondPublicIds.length &&
      firstPublicIds.every((publicId, index) => publicId === secondPublicIds[index])
    );
  });
}

function buildRoomImages(
  images: PropertyRoomTypeWithImages['images'],
  excludedImagePublicIds: Array<string> = [],
): Array<RoomImage> {
  const seenPublicIds = new Set<string>();
  const excludedPublicIds = new Set(excludedImagePublicIds);

  return (images ?? []).reduce<Array<RoomImage>>((roomImages, image: MediaSourceImage) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || seenPublicIds.has(publicId) || excludedPublicIds.has(publicId)) {
      return roomImages;
    }

    seenPublicIds.add(publicId);
    roomImages.push({
      publicId,
      title: image.title || undefined,
    });

    return roomImages;
  }, []);
}

export function buildRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
): RoomImagesByKey {
  return propertyRoomTypes.reduce<RoomImagesByKey>((roomImagesByKey, room, index) => {
    roomImagesByKey[getRoomKey(room, index)] = buildRoomImages(room.images, excludedImagePublicIds).slice(
      0,
      ROOM_IMAGE_LIMIT,
    );
    return roomImagesByKey;
  }, {});
}

export function pruneUnavailableRoomImages(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
): RoomImagesByKey {
  const availablePublicIds = new Set(mediaImages.map((image) => image.publicId));

  return Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages.filter((image) => availablePublicIds.has(image.publicId)),
    ]),
  );
}
